<template>
  <div class="sub-category-tricks-page container">
    <div>
      <TheBreadcrumb :pages="arianeRoute" />
      <HeaderCommunity :page="currentPageInfo" />
      <SearchResults
        :is-loading="isLoading"
        placeholder-text="Rechercher une astuce"
        @search-update="searchUpdateHandler"
      >
        <template #results>
          <CardContainer
            :items="communityTricksSubcategories"
            :no-result-text="noContent"
          />
        </template>
      </SearchResults>
      <ThePagination
        :total-pages="communityTricksSubcategoriesPages"
        :current-page="communityTricksSubcategoriesCurrentPage"
        :go-to="changePageTo"
      />
      <PegiWarning v-if="community?.pegi === '18'" />
    </div>
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  middleware: async (to) => {
    const communityStore = useCommunitiesStore()
    const tricksStore = useTricksStore()
    const communityName = to.params.communityName as string

    await Promise.all([
      // Fetch data about the community
      communityStore.fetchCommunity({
        slug: communityName,
      }),
      // Fetch tricks categories from the community
      tricksStore.fetchTricksCommunitySubcategories(communityName),
      // and of the whole site, to tell an unknown subcategory from an empty one
      tricksStore.fetchTricksSubcategories(),
      tricksStore.fetchCommunitySubcategoriesTricks({
        page: to.query.page as unknown as number,
        community: communityName,
        subCategory: to.params.subCategoryName as string,
      }),
    ])

    throwIfNotFound(communityStore.community)

    // A subcategory unknown to the site (e.g. /astuces/null, asked by crawlers) is a 404, not an empty listing
    // answered 200; a subcategory with no trick in this community keeps its empty listing. Nothing is decided
    // when the list could not be fetched.
    const subCategories = tricksStore.tricksSubcategoriesList
    if (
      subCategories.length > 0 &&
      !subCategories.some((subCat) => subCat.slug === to.params.subCategoryName)
    ) {
      throw createError({ statusCode: 404 })
    }

    if (tricksStore.getCategoryPagesCount === -1) {
      return navigateTo(to.path, { replace: true })
    }
  },
})

const tricksStore = useTricksStore()
const {
  communityTricksSubcategoriesPages,
  communityTricksSubcategoriesCurrentPage,
  communityTricksSubcategories,
} = storeToRefs(tricksStore)

const communitiesStore = useCommunitiesStore()
const { community } = storeToRefs(communitiesStore)

const route = useRoute()
const slug = computed(() => route.params.subCategoryName as string)

const subCategory = computed(() => tricksStore.getSubCategoryBySlug(slug).value)
const subcategorySlug = computed(() => subCategory.value?.slug)

const pageTitle = computed(() => {
  return subCategory?.value?.name ?? 'Toutes les astuces'
})

const communityName = computed(() => route.params.communityName as string)
const arianeRoute = computed(() => {
  return [
    {
      title: 'Toutes les communautés',
      route: '/communautes',
    },
    {
      title: community.value?.title,
      route: `/communautes/${community.value?.slug}`,
    },
    {
      title: 'Les astuces',
      route: `/communautes/${communityName.value}/astuces`,
    },
    {
      title: pageTitle.value,
      route: ``,
    },
  ]
})

const breadcrumbSchema = useBreadcrumbSchema({
  arianeRoute,
})

const link = useLinkPrevNext({
  currentPage: communityTricksSubcategoriesCurrentPage,
  pagesCount: communityTricksSubcategoriesPages,
})

useHead({
  title: 'Les astuces',
  link,
  script: [breadcrumbSchema],
})

useSeoMeta({
  title: `Astuces, tips et solutions « ${subcategorySlug.value} » ${community.value?.title}`,
  description: `Découvrez toutes les solutions, tips et astuces « ${subcategorySlug.value} » sur ${community.value?.title} : infos, avis, forum, challenges et vidéos.`,
  ogTitle: `Toute les astuces « ${subcategorySlug.value} » sur ${community.value?.title}`,
  ogDescription: `Découvrez toutes les solutions, tips et astuces « ${subcategorySlug.value} » sur ${community.value?.title}.`,
  ogImage:
    community.value?.image_desktop?.url ??
    'https://www.weareplaystation.fr/img/og-img.jpg',
})

const referentialStore = useReferentialStore()
const { referential } = storeToRefs(referentialStore)

const currentPageInfo = computed(() => {
  const { image, imageMobile } = getImages(community.value)

  return {
    title: 'Les astuces',
    subcategory: pageTitle.value,
    game: community.value?.title,
    pegi: community.value?.pegi,
    members: community.value?.users_count,
    image,
    imageMobile,
    contribPoints: referential.value?.points.post_source_create,
    description: `Découvrez toutes les solutions, tips et astuces « ${subcategorySlug.value} » de la communauté sur ${community.value?.title}.`,
  }
})

const searchData = async () => {
  isLoading.value = true

  await tricksStore.fetchCommunitySubcategoriesTricks({
    search: search.value,
    subCategory: slug.value,
    community: slug.value,
  })

  if (!communityTricksSubcategories.value?.length) {
    noContent.value = !search.value
      ? 'Aucun contenu'
      : 'Aucun résultat pour votre recherche'
  }

  isLoading.value = false
}

const { search, isLoading, noContent, searchUpdateHandler } = useSearching({
  searchData,
})

const changePageTo = async (page: number) => {
  if (page > 1) {
    await tricksStore.fetchCommunitySubcategoriesTricks({
      page,
      search: search.value,
      community: slug.value,
      subCategory: slug.value,
    })
  }
}
</script>

<style lang="scss" scoped>
.sub-category-tricks-page {
  min-height: 100vh;
  padding-bottom: 10rem;
}
</style>
